import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import type { POI, LocationRef, AccommodationConfig } from '@poi-planner/shared';
import { getAccommodationPalette } from '../utils/colors';

interface LocationOptionProps {
  id: string;
  name: string;
  type: 'poi' | 'accommodation';
  isSelected: boolean;
  onSelect: (ref: LocationRef) => void;
  icon: string;
  colorDot?: string; // Accommodation color
}

function LocationOption({ id, name, type, isSelected, onSelect, icon, colorDot }: LocationOptionProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect({ type, id })}
      className={clsx(
        'w-full px-3.5 py-2.5 flex items-center gap-2.5 border-none border-b border-border cursor-pointer text-[13px] font-sans text-text-primary text-left transition-colors hover:bg-input-bg',
        isSelected && 'bg-input-bg'
      )}
    >
      {colorDot ? (
        <span
          className="w-4 h-4 rounded-full shrink-0 border border-white/50 shadow-sm"
          style={{ backgroundColor: colorDot }}
        />
      ) : (
        <span
          className={clsx(
            'material-symbols-outlined text-lg',
            isSelected ? 'text-text-primary' : 'text-text-secondary'
          )}
        >
          {icon}
        </span>
      )}
      <span className={clsx('flex-1 overflow-hidden text-ellipsis whitespace-nowrap', isSelected && 'font-semibold')}>
        {name}
      </span>
      {isSelected && (
        <span className="material-symbols-outlined text-lg text-green-500">
          check
        </span>
      )}
    </button>
  );
}

interface SectionHeaderProps {
  icon: string;
  label: string;
}

function SectionHeader({ icon, label }: SectionHeaderProps) {
  return (
    <div className="px-3.5 py-2 pb-1.5 text-[10px] font-bold text-text-muted uppercase tracking-widest bg-input-bg border-b border-border flex items-center gap-1.5">
      <span className="material-symbols-outlined text-sm opacity-60">{icon}</span>
      {label}
    </div>
  );
}

interface LocationPickerProps {
  selected: LocationRef | null;
  onSelect: (ref: LocationRef) => void;
  onCreateNew?: () => void;
  pois: POI[];
  accommodationConfigs: Record<string, AccommodationConfig>;
  placeholder?: string;
  label?: string;
}

export function LocationPicker({
  selected,
  onSelect,
  onCreateNew,
  pois,
  accommodationConfigs,
  placeholder = 'Select a location...',
  label,
}: LocationPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Separate accommodation POIs from regular POIs
  const accommodationPOIs = pois.filter((p) =>
    p.tags?.some((t) => t.toLowerCase() === 'accommodation')
  );
  const nonAccommodationPOIs = pois.filter((p) =>
    !p.tags?.some((t) => t.toLowerCase() === 'accommodation')
  );

  // Resolve selected item for display
  const selectedItem = selected
    ? (() => {
        // Both 'poi' and 'accommodation' types now point to POIs
        const poi = pois.find((p) => p.id === selected.id);
        if (poi) {
          return { name: poi.name, type: selected.type };
        }
        return null;
      })()
    : null;

  // Filter by search query
  const query = searchQuery.toLowerCase();
  const filteredAccommodations = accommodationPOIs.filter((a) =>
    a.name.toLowerCase().includes(query)
  );
  const filteredNonAccommodations = nonAccommodationPOIs.filter((p) =>
    p.name.toLowerCase().includes(query)
  );

  // Separate non-accommodation POIs into transport hubs and other locations
  const transportHubs = filteredNonAccommodations.filter((poi) =>
    poi.tags?.some((tag) => tag.toLowerCase() === 'transport')
  );
  const otherLocations = filteredNonAccommodations.filter((poi) =>
    !poi.tags?.some((tag) => tag.toLowerCase() === 'transport')
  );

  const hasResults = filteredAccommodations.length > 0 || filteredNonAccommodations.length > 0;

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (ref: LocationRef) => {
    onSelect(ref);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleCreateNew = () => {
    setIsOpen(false);
    setSearchQuery('');
    onCreateNew?.();
  };

  const getSelectedIcon = () => {
    if (!selected) return 'location_on';
    const poi = pois.find((p) => p.id === selected.id);
    if (!poi) return 'location_on';
    if (poi.tags?.some((t) => t.toLowerCase() === 'accommodation')) return 'hotel';
    if (poi.tags?.some((t) => t.toLowerCase() === 'transport')) return 'connecting_airports';
    return 'location_on';
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="form-label">
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'w-full px-3 py-2.5 flex items-center justify-between gap-2 bg-input-bg border border-border rounded-lg cursor-pointer text-[13px] font-sans text-left transition-all',
          selectedItem ? 'text-text-primary' : 'text-text-muted',
          isOpen ? 'shadow-[0_0_0_2px_var(--color-text-primary)]' : 'hover:border-text-secondary'
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={clsx('material-symbols-outlined text-lg shrink-0', selectedItem ? 'text-text-secondary' : 'text-text-muted')}>
            {getSelectedIcon()}
          </span>
          <span className={clsx('overflow-hidden text-ellipsis whitespace-nowrap', selectedItem && 'font-medium')}>
            {selectedItem?.name ?? placeholder}
          </span>
        </div>
        <span
          className={clsx(
            'material-symbols-outlined text-lg text-text-muted shrink-0 transition-transform',
            isOpen && 'rotate-180'
          )}
        >
          expand_more
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-xl shadow-lg z-[100] overflow-hidden animate-[locationPickerFadeIn_0.15s_ease]">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="flex items-center gap-2 px-2.5 py-2 bg-input-bg rounded-lg">
              <span className="material-symbols-outlined text-lg text-text-muted">
                search
              </span>
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search locations..."
                className="flex-1 border-none bg-transparent text-[13px] font-sans text-text-primary outline-none"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="bg-transparent border-none p-0 cursor-pointer text-text-muted flex"
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {!hasResults && searchQuery && (
              <div className="p-4 text-center text-text-muted text-[13px]">
                No locations found
              </div>
            )}

            {/* Accommodations Section */}
            {filteredAccommodations.length > 0 && (
              <>
                <SectionHeader icon="hotel" label="Accommodations" />
                {filteredAccommodations.map((poi) => {
                  const config = accommodationConfigs[poi.id];
                  const palette = getAccommodationPalette(config);
                  return (
                    <LocationOption
                      key={poi.id}
                      id={poi.id}
                      name={poi.name}
                      type="accommodation"
                      isSelected={selected?.id === poi.id}
                      onSelect={handleSelect}
                      icon="hotel"
                      colorDot={palette.text}
                    />
                  );
                })}
              </>
            )}

            {/* Transport Hubs Section */}
            {transportHubs.length > 0 && (
              <>
                <SectionHeader icon="connecting_airports" label="Transport Hubs" />
                {transportHubs.map((poi) => (
                  <LocationOption
                    key={poi.id}
                    id={poi.id}
                    name={poi.name}
                    type="poi"
                    isSelected={selected?.type === 'poi' && selected.id === poi.id}
                    onSelect={handleSelect}
                    icon="connecting_airports"
                  />
                ))}
              </>
            )}

            {/* Other Locations Section */}
            {otherLocations.length > 0 && (
              <>
                <SectionHeader icon="location_on" label="Other Locations" />
                {otherLocations.map((poi) => (
                  <LocationOption
                    key={poi.id}
                    id={poi.id}
                    name={poi.name}
                    type="poi"
                    isSelected={selected?.type === 'poi' && selected.id === poi.id}
                    onSelect={handleSelect}
                    icon="location_on"
                  />
                ))}
              </>
            )}
          </div>

          {/* Create New Option */}
          {onCreateNew && (
            <button
              type="button"
              onClick={handleCreateNew}
              className="w-full px-3.5 py-3 flex items-center gap-2.5 bg-transparent border-none border-t border-border cursor-pointer text-[13px] font-semibold font-sans text-text-primary text-left transition-colors hover:bg-input-bg"
            >
              <span className="material-symbols-outlined text-lg text-blue-500">
                add_circle
              </span>
              <span>Create new POI...</span>
            </button>
          )}
        </div>
      )}

      <style>{`
        @keyframes locationPickerFadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
